// Reactive request primitive for the Svelte 5 SPA.
//
// `createQuery(fetcher)` returns an object with reactive `status`, `data`,
// and `error` fields. The fetcher fires in the constructor so by the time
// the component renders, status is already `'loading'`. There's no $effect
// to wire up in the calling component, which avoids the lifecycle / HMR
// gotchas we hit mixing top-level IIFEs with `{:else}` blocks (see
// memory/svelte5-derived-async-quirk.md).
//
// Status flow:
//
//   idle ──► loading ──► completed   (fetcher resolved; data is set)
//                  └──► failed       (fetcher threw; error is set)
//
// Race guard: each refetch bumps an internal nonce. If a slower in-flight
// request resolves after a newer one has been kicked off, its result is
// discarded.
//
// Reactive args: pass `deps: () => [a, b]` and the query auto-refetches
// whenever any dep changes. The library is the only place that touches
// $effect — components never have to.
//
// Mutations: `createMutation(fn)` returns the same shape but does NOT
// fire on creation. Call `.mutate(args)` to trigger; the returned promise
// resolves to the result (or null if a newer mutate superseded it).
//
// Filename ends in `.svelte.ts` because Svelte 5 only honours runes
// (`$state`, `$effect`) in `.svelte` / `.svelte.ts` files.

export type QueryStatus = 'idle' | 'loading' | 'completed' | 'failed';

export interface QueryOptions {
	/**
	 * If true, the query does NOT fire on construction. Call `.refetch()`
	 * (or pass `deps`) to kick it off later.
	 */
	lazy?: boolean;
	/**
	 * Function that reads any reactive values the fetcher depends on.
	 * When the result of this function changes (by Svelte's deep-equality
	 * tracking inside an `$effect`), the query refetches automatically.
	 * Use this for filtered / parameterised endpoints.
	 *
	 *   const q = createQuery(
	 *     () => events.list({ ip: clientIp }),
	 *     { deps: () => [clientIp] },
	 *   );
	 */
	deps?: () => unknown;
}

export class Query<T> {
	status: QueryStatus = $state('idle');
	data: T | null = $state(null);
	error: unknown = $state(null);

	#fetcher: () => Promise<T>;
	#nonce = 0;

	constructor(fetcher: () => Promise<T>, opts: QueryOptions = {}) {
		this.#fetcher = fetcher;

		if (opts.deps) {
			// Reactive mode: $effect re-runs whenever any reactive value touched
			// by opts.deps() changes. Each run kicks off a refetch; the nonce
			// guard discards stale results.
			$effect(() => {
				opts.deps!();
				void this.refetch();
			});
		} else if (!opts.lazy) {
			// One-shot: fire once at construction time. No $effect needed; the
			// component does nothing.
			void this.refetch();
		}
	}

	/**
	 * Re-run the fetcher. Always safe to call — concurrent invocations are
	 * race-guarded.
	 */
	refetch = async (): Promise<void> => {
		const my = ++this.#nonce;
		this.status = 'loading';
		this.error = null;
		try {
			const result = await this.#fetcher();
			if (my !== this.#nonce) return;
			this.data = result;
			this.status = 'completed';
		} catch (err) {
			if (my !== this.#nonce) return;
			this.error = err;
			this.status = 'failed';
		}
	};

	/** Clear all reactive fields back to the `idle` shape. */
	reset(): void {
		this.#nonce++;
		this.status = 'idle';
		this.data = null;
		this.error = null;
	}

	/**
	 * Human-readable error message, for templates. Returns '' when there is
	 * no error.
	 */
	get errorMessage(): string {
		if (!this.error) return '';
		if (this.error instanceof Error) return this.error.message;
		return String(this.error);
	}
}

export function createQuery<T>(
	fetcher: () => Promise<T>,
	opts?: QueryOptions,
): Query<T> {
	return new Query(fetcher, opts);
}

// ---------------------------------------------------------------------------

export class Mutation<TArgs extends unknown[], T> {
	status: QueryStatus = $state('idle');
	data: T | null = $state(null);
	error: unknown = $state(null);

	#fn: (...args: TArgs) => Promise<T>;
	#nonce = 0;

	constructor(fn: (...args: TArgs) => Promise<T>) {
		this.#fn = fn;
	}

	/**
	 * Invoke the wrapped function. Resolves to the result on success, or
	 * `null` if a newer `mutate(...)` superseded this call (race guard).
	 * The returned promise never rejects — inspect `.status` / `.error`
	 * for the failure path.
	 */
	mutate = async (...args: TArgs): Promise<T | null> => {
		const my = ++this.#nonce;
		this.status = 'loading';
		this.error = null;
		try {
			const result = await this.#fn(...args);
			if (my !== this.#nonce) return null;
			this.data = result;
			this.status = 'completed';
			return result;
		} catch (err) {
			if (my !== this.#nonce) return null;
			this.error = err;
			this.status = 'failed';
			return null;
		}
	};

	reset(): void {
		this.#nonce++;
		this.status = 'idle';
		this.data = null;
		this.error = null;
	}

	get errorMessage(): string {
		if (!this.error) return '';
		if (this.error instanceof Error) return this.error.message;
		return String(this.error);
	}
}

export function createMutation<TArgs extends unknown[], T>(
	fn: (...args: TArgs) => Promise<T>,
): Mutation<TArgs, T> {
	return new Mutation(fn);
}
